import { saveBuylead } from '@/lib/repository/crm/CRMRepository';
import { ProductItem } from '@/lib/types/api';
import useTranslation from 'next-translate/useTranslation';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import Drawer from '../ui/Modals/Drawer/Drawer';
import { Input } from './Input';
import { InputTerms } from './InputTerms';
import { SelectCountry } from './SelectCountry';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { setIsGetQuoteModalOpen } from './NewsLetter/modalSlice';
import { bingUET } from '@/lib/utilities/analytics/bing';
import { SuccessCheck } from './Success/SuccessCheck';
import {
  getGTMProductData,
  pushDataLayer,
} from '@/lib/utilities/analytics/gtm';
import useUserMetadata from '@/lib/hooks/useUserData';
import { useUser } from '@auth0/nextjs-auth0/client';
import COUNTRY_NAMES from '@/lib/data/country-names.json';
import { useBoolean } from 'react-use';
import { LinkEditMetadata } from '../ui/Links/LinkEditMetadata';
import { hasPhonePrefix } from '@/lib/data-transformer/gindumacUtils';

function GetQuoteForm({ product }: { product: ProductItem }) {
  const { isGetQuoteModalOpen: isOpen } = useAppSelector(
    (state) => state.modals
  );
  const { t } = useTranslation('products');
  const dispatch = useAppDispatch();

  interface FormData {
    'quote-first-name': string;
    'quote-last-name': string;
    'quote-transport': boolean;
    'quote-address'?: string;
    'quote-installment': boolean;
    'quote-inspection': boolean;
    'quote-renting': boolean;
    'quote-city': string;
    'quote-email': string;
    'quote-company': string;
    'quote-phone-prefix': string;
    'quote-phone': string;
    'quote-country': string;
  }
  const { user } = useUser();
  const { metadata } = useUserMetadata();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitSuccessful, isSubmitting },
    reset,
    watch,
  } = useForm<FormData>({
    mode: 'onBlur',
  });
  const [hasPrefixInput, setHasPrefixInput] = useBoolean(true);
  const hasQuoteAddressInput =
    watch('quote-installment') && !watch('quote-transport');
  useEffect(() => {
    function handleReset() {
      const phone = metadata?.phone ?? metadata?.companyPhone;
      const phoneHasSomePrefix = hasPhonePrefix(phone);
      setHasPrefixInput(!phoneHasSomePrefix);
      reset({
        'quote-first-name': metadata?.firstName,
        'quote-last-name': metadata?.lastName,
        'quote-company': metadata?.companyName,
        'quote-city': metadata?.city ?? metadata?.companyCity,
        'quote-email': user?.email ?? metadata?.companyEmail,
        'quote-phone': phone,
        'quote-country': COUNTRY_NAMES.find(
          (e) => e[1] === (metadata?.country ?? metadata?.companyCountry)
        )?.[0],
      });
    }
    // run handleReset after one sec
    const timer = setTimeout(() => handleReset(), 1000);
    return () => clearTimeout(timer);
  }, [metadata, reset, isOpen, user?.email, setHasPrefixInput]);

  async function submit(data: FormData) {
    console.log(data);
    try {
      await saveBuylead({
        'first-name': data['quote-first-name'],
        'last-name': data['quote-last-name'],
        sku: product.sku,
        city: data['quote-city'],
        email: data['quote-email'],
        company: data['quote-company'],
        country: data['quote-country'],
        'phone-number': data['quote-phone'],
        'phone-prefix': data['quote-phone-prefix'],
        'transport-address': data['quote-address'],
        'platform-name': 'GINDUMAC Website',
        'platform-type': 'Quote Request',
        inspection: data['quote-inspection'],
        finance: data['quote-renting'],
        transport: data['quote-transport'],
        'on-site-installment': data['quote-installment'],
      });
      const formType = 'Get a quote';
      pushDataLayer('formSubmission', {
        formType,
      });
      pushDataLayer('begin_checkout', {
        formType,
        ecommerce: {
          value: product.price,
          currency: product.currency,
          items: [getGTMProductData(product)],
        },
      });
    } catch (error) {
      console.log(error);
    }
  }

  function openModal() {
    dispatch(setIsGetQuoteModalOpen(true));
    pushDataLayer('formInit', {
      formType: 'Get a quote',
    });
    bingUET('Get an official quote');
  }

  return (
    <>
      <div
        className={
          'cursor-pointer rounded bg-success-500 px-6 py-6 uppercase text-white hover:bg-success-400'
        }
        onClick={() => openModal()}
        data-testid='open-modal-get-quote'
      >
        {t('products:DETAIL_ASIDE_BTN_GETQUOTE')}
      </div>
      <Drawer
        hasLogo
        isOpen={isOpen}
        setIsOpen={(isOpen: boolean) =>
          dispatch(setIsGetQuoteModalOpen(isOpen))
        }
        size='full'
      >
        <hr />
        <div className='' data-testid='get-quote-form'>
          <h1 className='my-6 text-center'>
            {t('products:DETAIL_MODALQUOTE_HEADER')}
          </h1>
          <form className='mx-auto max-w-xl' onSubmit={handleSubmit(submit)}>
            {isSubmitSuccessful ? (
              <SuccessCheck />
            ) : (
              <>
                <FormTitle title={t('products:DETAIL_MODALQUOTE_DETAILS')} />
                <h3
                  className='my-6 text-center'
                  dangerouslySetInnerHTML={{
                    __html: t('products:DETAIL_MODALQUOTE_SUBHEADER_LOGISTICS'),
                  }}
                />
                <div className='flex flex-col items-center justify-center'>
                  <Input
                    type='checkbox'
                    name='quote-transport'
                    register={register}
                    errors={errors}
                    label={t('products:DETAIL_MODALQUOTE_TRANSPORT')}
                    isHorizontal={true}
                  />
                  {watch('quote-transport') && (
                    <div className='my-6 w-full'>
                      <Input
                        name='quote-address'
                        errors={errors}
                        placeholder={t('products:FORM_PLACEHOLDER_LOCATION')}
                        register={register}
                        validation={{
                          required: true,
                        }}
                      />
                    </div>
                  )}
                </div>
                <h3
                  className='my-6 text-center !font-extralight'
                  dangerouslySetInnerHTML={{
                    __html: t('products:DETAIL_MODALQUOTE_SUBHEADER1'),
                  }}
                />
                <div className='mb-6 flex flex-col items-center justify-center space-y-2'>
                  <div className='space-y-4'>
                    <Input
                      type='checkbox'
                      name='quote-installment'
                      register={register}
                      errors={errors}
                      label={t('products:DETAIL_MODALQUOTE_INSTALLMENT')}
                    />
                    {hasQuoteAddressInput && (
                      <div className='my-6 w-full'>
                        <Input
                          name='quote-address'
                          errors={errors}
                          placeholder={t('products:FORM_PLACEHOLDER_ADDRESS')}
                          register={register}
                        />
                      </div>
                    )}
                    <Input
                      type='checkbox'
                      name='quote-inspection'
                      register={register}
                      errors={errors}
                      label={t('products:DETAIL_MODALQUOTE_INSPECTION')}
                    />
                    <Input
                      type='checkbox'
                      name='quote-renting'
                      register={register}
                      errors={errors}
                      label={t('products:DETAIL_MODALQUOTE_RENTING')}
                    />
                  </div>
                </div>
                <div className='space-y-2'>
                  <h3 className='my-4 text-center'>
                    {t('products:DETAIL_MODALQUOTE_SUBHEADER3')}
                  </h3>
                  <LinkEditMetadata />
                  <div className='flex space-x-2'>
                    <div className='w-1/2'>
                      <Input
                        name='quote-first-name'
                        register={register}
                        errors={errors}
                        placeholder={t('products:FORM_PLACEHOLDER_FIRSTNAME')}
                        validation={{
                          required: true,
                          maxLength: 50,
                        }}
                      />
                    </div>
                    <div className='w-1/2'>
                      <Input
                        name='quote-last-name'
                        register={register}
                        errors={errors}
                        placeholder={t('products:FORM_PLACEHOLDER_LASTNAME')}
                        validation={{
                          required: true,
                          maxLength: 50,
                        }}
                      />
                    </div>
                  </div>
                  <div className='flex space-x-2'>
                    <div className='w-1/2'>
                      <Input
                        name='quote-company'
                        register={register}
                        errors={errors}
                        placeholder={t('products:FORM_PLACEHOLDER_COMPANY')}
                        validation={{
                          required: true,
                          maxLength: 100,
                        }}
                      />
                    </div>
                    <div className='w-1/2'>
                      <Input
                        type='email'
                        name='quote-email'
                        register={register}
                        errors={errors}
                        placeholder={t('products:FORM_PLACEHOLDER_EMAIL')}
                        validation={{
                          required: true,
                          maxLength: 100,
                        }}
                      />
                    </div>
                  </div>
                  <div className='flex space-x-2'>
                    {hasPrefixInput && (
                      <div className='w-1/2'>
                        <SelectCountry
                          type='phoneCode'
                          name='quote-phone-prefix'
                          register={register}
                          errors={errors}
                          validation={{
                            required: true,
                          }}
                        />
                      </div>
                    )}

                    <div className={hasPrefixInput ? 'w-1/2' : 'w-full'}>
                      <Input
                        type={hasPrefixInput ? 'number' : 'text'}
                        name='quote-phone'
                        register={register}
                        errors={errors}
                        placeholder={t('products:FORM_PLACEHOLDER_PHONE')}
                        validation={{
                          required: true,
                          maxLength: 20,
                        }}
                      />
                    </div>
                  </div>
                  <div className='flex space-x-2'>
                    <div className='w-1/2'>
                      <Input
                        name='quote-city'
                        register={register}
                        errors={errors}
                        placeholder={t(
                          'products:FORM_PLACEHOLDER_CITY'
                        ).toUpperCase()}
                        validation={{
                          required: true,
                          maxLength: 100,
                        }}
                      />
                    </div>
                    <div className='w-1/2'>
                      <SelectCountry
                        type='country'
                        name='quote-country'
                        register={register}
                        errors={errors}
                        validation={{
                          required: true,
                        }}
                      />
                    </div>
                  </div>
                  <InputTerms
                    name='quote-terms'
                    register={register}
                    errors={errors}
                  />
                </div>
                <button
                  type='submit'
                  className='mx-auto mb-12 mt-4 rounded bg-brand-500 px-8 py-4 text-center uppercase text-white hover:bg-brand-400'
                  disabled={isSubmitting}
                >
                  {t('products:DETAIL_MODALQUOTE_BTN_SEND')}
                </button>
              </>
            )}
          </form>
        </div>
      </Drawer>
    </>
  );
}

export function FormTitle({ title }: { title: string }) {
  return (
    <div>
      <p className='text-center text-muted-500'>{title}</p>
    </div>
  );
}

export default GetQuoteForm;
